import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useUser, useClerk } from "@clerk/clerk-react";
import { supplyChainService } from "../services/apiClient";
import { useToast } from "../contexts/ToastContext";
import {
  calculateProductMetrics,
  batchCalculateMetrics,
  SAFETY_STOCK_DAYS,
  RawProductData,
  RawSalesRecord,
  calculateOOSDate,
  calculateReorderLogic,
  formatCurrency,
  formatDate,
  getDaysUntil,
  calculateDemandGrowthRate,
  calculateForecastStock
} from "../utils/inventoryCalculations";
import {
  rawDataCache,
  calculationCache,
  salesDataCache,
  CacheKeys,
  initializeCaches
} from "../utils/clientCache";
import {
  useRawDataCombined,
  useLocalStockManagement,
  useClearCache,
  usePrefetchSupplyChainData,
  useCacheManager
} from "../hooks/useInventoryQueries";
import { SalesData, DemandData } from "../types/supplyChain";
import { ProductMetrics } from "../types/api";
import OrderManagement from "./OrderManagement";
import ShippingAnalytics from "./ShippingAnalytics";
import StockGraphModal from "./StockGraphModal";
import ReplenishmentRecommendations from "./ReplenishmentRecommendations";
import UnifiedOrderManagement from "./UnifiedOrderManagement";
import ProductMixChart from "./ProductMixChart";
import Settings from "./Settings";

// product mapping to get SKUs for each product — must match backend product_mapping.py
const PRODUCT_SKU_MAPPING: { [key: string]: string[] } = {
  "ADRQ Series": ["ADRQ-2024001", "ADRQ-2024002", "ADRQ-46884785", "ADRQ-2024003", "ADRQ-11884785"],
  "ARP Series": ["ARP-TWP-L", "ARP-CS-BS", "ARP-CS-JP", "ARP-NK", "ARP-K1"],
  "ARR Series": ["ARR-HC-1", "ARR-RC-01", "ARR-RC-02", "ARR-RC-03", "ARR-PB"],
  "RG Series": ["RG-Brain", "RG-Gut", "RG-Heart"],
  "NHH": ["NHH"],  // Bundle SKUs (1NHH2B etc.) are sales-only, physical inventory tracked as NHH
  "Marketing Materials": ["brochure"]
};

interface LocalStockData {
  warehouse: string;
  total_products: number;
  products: ProductMetrics[];
  timestamp: string;
  data_source?: string;
  calculation_method?: string;
}

interface CentralStockData {
  total_products: number;
  products: Array<{
    product_name: string;
    reorder_flagged: boolean;
    reorder_amount_required: number;
    earliest_cutoff_date: string | null;
    warehouses: Array<{
      warehouse: string;
      reorder_flagged: boolean;
      reorder_amount: number;
      cutoff_date: string;
      current_stock: number;
      daily_run_rate: number;
      days_until_oos: number;
    }>;
  }>;
  summary: {
    reorder_flagged_count: number;
    total_reorder_amount: number;
  };
  timestamp: string;
  data_source?: string;
  calculation_method?: string;
}

interface OrdersData {
  total_orders_needed: number;
  orders: Array<{
    product_name: string;
    country: string;
    quantity_needed: number;
    quantity_in_progress?: number;
    order_cutoff_date: string;
    days_until_cutoff: number;
    reorder_flagged: boolean;
    priority: string;
    manufacturing_time: number;
    estimated_cost: number;
    current_stock: number;
    daily_run_rate: number;
    countries_details?: any;
  }>;
  urgent_orders: Array<any>;
  timestamp: string;
  calculation_method: string;
}

interface DashboardProps {
  onLogout?: () => void;
}

interface UrgentTickerItem {
  key: string;
  text: string;
}

const URGENT_TICKER_CACHE_KEY = "urgent_ticker_cache_v1";
const URGENT_TICKER_CACHE_TTL_MS = 10 * 60 * 1000;

const Dashboard: React.FC<DashboardProps> = ({ onLogout }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  // Check if Clerk is configured
  const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY || '';
  const isClerkConfigured = clerkPubKey && clerkPubKey.startsWith('pk_');

  // Use Clerk hooks (safe to call even if not configured)
  const { user } = useUser();
  const { signOut } = useClerk();

  // Handle logout for both Clerk and non-Clerk modes
  const handleLogout = () => {
    if (isClerkConfigured && signOut) {
      signOut();
    } else if (onLogout) {
      onLogout();
    }
  };
  const location = useLocation();

  // Get active section from URL path
  const getActiveSection = (): string => {
    const path = location.pathname;
    if (path === "/" || path === "/local-stock") return "local";
    if (path === "/central-stock") return "central";
    // Unified: orders and order-management map together (NOT replenishment)
    if (path === "/orders" || path === "/order-management") return "order-management";
    if (path === "/replenishment") return "replenishment";
    if (path === "/forecast") return "forecast";
    if (path === "/shipping-analytics") return "shipping-analytics";
    if (path === "/product-analytics") return "product-analytics";
    if (path === "/unit-economics" || path.startsWith("/unit-economics/")) return "unit-economics";
    return "local"; // default
  };

  const activeSection = getActiveSection();

  const [selectedWarehouse, setSelectedWarehouse] = useState<string>("UK");
  const [inputDate, setInputDate] = useState<string>(new Date().toISOString().split("T")[0]);
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split("T")[0]);
  // Debounce: only trigger data fetch 800ms after user stops changing date
  useEffect(() => {
    const timer = setTimeout(() => setSelectedDate(inputDate), 800);
    return () => clearTimeout(timer);
  }, [inputDate]);
  const [warehouseDropdownOpen, setWarehouseDropdownOpen] = useState<boolean>(false);
  const [localStockData, setLocalStockData] = useState<LocalStockData | null>(null);
  const [centralStockData, setCentralStockData] = useState<CentralStockData | null>(null);
  const [ordersData, setOrdersData] = useState<OrdersData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [calculating, setCalculating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [editingOrderIndex, setEditingOrderIndex] = useState<number | null>(null); // Track which order is being edited
  const [editedQuantities, setEditedQuantities] = useState<{ [key: number]: number }>({}); // Track edited quantities
  const [isClearingCache, setIsClearingCache] = useState<boolean>(false);
  const [isSyncingBigQuery, setIsSyncingBigQuery] = useState<boolean>(false);
  const [warehouseData, setWarehouseData] = useState<Map<string, LocalStockData>>(new Map());
  const [lastRequestTime, setLastRequestTime] = useState<number>(0);
  const [calculationStatus, setCalculationStatus] = useState<string>("Ready");
  const [stockGraphSku, setStockGraphSku] = useState<{ sku: string, warehouse: string } | null>(null);
  const [skuInStockRates, setSkuInStockRates] = useState<Map<string, { rate90: number, rate180: number }>>(new Map());
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());
  const [expandedCentralProducts, setExpandedCentralProducts] = useState<Set<string>>(new Set());
  const [skuData, setSkuData] = useState<Map<string, any>>(new Map());
  const [loadingSkuData, setLoadingSkuData] = useState<Set<string>>(new Set());
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true); // Sidebar state
  const [forecastViewMode, setForecastViewMode] = useState<'forecast' | 'recommendations'>('forecast'); // Forecast tab view mode
  const [urgentTickerItems, setUrgentTickerItems] = useState<UrgentTickerItem[]>([]);
  const [urgentTickerUpdatedAt, setUrgentTickerUpdatedAt] = useState<string>("");
  const warehouseDropdownRef = React.useRef<HTMLDivElement>(null);
  const activeSectionRef = React.useRef<string>(activeSection);

  const loggedInDisplayName = useMemo(() => {
    if (isClerkConfigured) {
      return user?.fullName || user?.primaryEmailAddress?.emailAddress || "User";
    }
    return localStorage.getItem('supply_chain_user') || "User";
  }, [isClerkConfigured, user]);

  const loggedInAvatarUrl = isClerkConfigured ? (user?.imageUrl || null) : null;
  const loggedInInitial = (loggedInDisplayName || "U").charAt(0).toUpperCase();
  const prefetchScheduledRef = React.useRef<boolean>(false);

  // Initialize TanStack Query hooks for enhanced caching
  const clearCacheMutation = useClearCache();
  const { prefetchWarehouseData, prefetchAllCommonData } = usePrefetchSupplyChainData();
  const { getCacheStats, clearSpecificCache } = useCacheManager();

  // TanStack Query for calculated data (used alongside existing cache)
  const {
    data: queryLocalData,
    isLoading: queryLocalLoading,
    error: queryLocalError,
    refetch: refetchLocalData
  } = useLocalStockManagement(selectedWarehouse, selectedDate);

  useEffect(() => {
    activeSectionRef.current = activeSection;
  }, [activeSection]);

  // Redirect root path to /local-stock, /unit-economics to /unit-economics/product-cogs
  useEffect(() => {
    if (location.pathname === "/") {
      navigate("/local-stock", { replace: true });
    } else if (location.pathname === "/unit-economics") {
      navigate("/unit-economics/global-settings", { replace: true });
    }
  }, [location.pathname, navigate]);

  // Close warehouse dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (warehouseDropdownRef.current && !warehouseDropdownRef.current.contains(event.target as Node)) {
        setWarehouseDropdownOpen(false);
      }
    };

    if (warehouseDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [warehouseDropdownOpen]);

  // Initialize once, then defer heavy prefetch to background.
  useEffect(() => {
    initializeCaches();

    if (!prefetchScheduledRef.current) {
      prefetchScheduledRef.current = true;
      const idleLoader = () => prefetchAllCommonData().catch(console.error);
      const idleApi = (window as any).requestIdleCallback;
      if (typeof idleApi === "function") {
        idleApi(idleLoader, { timeout: 15000 });
      } else {
        setTimeout(idleLoader, 12000);
      }
    }

    // Listen for localStorage changes to refresh Orders data.
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'supply_chain_orders' && activeSectionRef.current === 'orders') {
        setTimeout(() => {
          loadOrdersData();
        }, 100);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Same-tab storage updates via custom event.
    const handleCustomStorageChange = (e: CustomEvent) => {
      if (e.detail.key === 'supply_chain_orders' && activeSectionRef.current === 'orders') {
        setTimeout(() => {
          loadOrdersData();
        }, 100);
      }
    };

    window.addEventListener('localStorageChange', handleCustomStorageChange as EventListener);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageChange', handleCustomStorageChange as EventListener);
    };
  }, []);

  const loadUrgentTicker = useCallback(async () => {
    try {
      const response = await supplyChainService.getReplenishmentRecommendations("all", 1, "high");
      const recommendations = Array.isArray(response?.recommendations) ? response.recommendations : [];

      const filtered = recommendations.filter((rec: any) => {
        const qty = Number(rec?.recommended_order_quantity || 0);
        const priority = String(rec?.priority || "").toLowerCase();
        return qty > 0 && (priority === "critical" || priority === "high");
      });

      const sorted = [...filtered].sort((a: any, b: any) => {
        const aDays = Number.isFinite(a?.days_until_reorder) ? a.days_until_reorder : 9999;
        const bDays = Number.isFinite(b?.days_until_reorder) ? b.days_until_reorder : 9999;
        return aDays - bDays;
      });

      const deduped = sorted.reduce((acc: any[], rec: any) => {
        const key = `${rec?.product_name || ""}-${rec?.warehouse || ""}`;
        if (!acc.some((x) => `${x?.product_name || ""}-${x?.warehouse || ""}` === key)) {
          acc.push(rec);
        }
        return acc;
      }, []);

      const items = deduped.slice(0, 12).map((rec: any, idx: number) => {
        const priority = String(rec?.priority || "").toUpperCase();
        const product = rec?.product_name || "Unknown Product";
        const warehouse = rec?.warehouse || "N/A";
        const date = rec?.recommended_order_date || "N/A";
        const qty = Number(rec?.recommended_order_quantity || 0).toLocaleString();
        const days = Number(rec?.days_until_reorder || 0);
        const dayLabel = days < 0 ? `${Math.abs(days)}d overdue` : `${days}d left`;
        return {
          key: `${product}-${warehouse}-${date}-${idx}`,
          text: `${priority}: ${product} (${warehouse}) reorder by ${date} | qty ${qty} | ${dayLabel}`
        };
      });

      if (items.length > 0) {
        setUrgentTickerItems(items);
      } else {
        setUrgentTickerItems([
          { key: "none", text: "No urgent reorder alerts right now." }
        ]);
      }

      const updatedAt = new Date().toLocaleTimeString();
      setUrgentTickerUpdatedAt(updatedAt);
      localStorage.setItem(URGENT_TICKER_CACHE_KEY, JSON.stringify({
        items: items.length > 0 ? items : [{ key: "none", text: "No urgent reorder alerts right now." }],
        updatedAt,
        timestamp: Date.now()
      }));
    } catch {
      setUrgentTickerItems([
        { key: "fallback", text: "Urgent reorder alerts are temporarily unavailable." }
      ]);
      setUrgentTickerUpdatedAt(new Date().toLocaleTimeString());
    }
  }, []);

  useEffect(() => {
    try {
      const cachedRaw = localStorage.getItem(URGENT_TICKER_CACHE_KEY);
      if (cachedRaw) {
        const cached = JSON.parse(cachedRaw);
        if (
          Array.isArray(cached?.items) &&
          typeof cached?.timestamp === "number" &&
          Date.now() - cached.timestamp < URGENT_TICKER_CACHE_TTL_MS
        ) {
          setUrgentTickerItems(cached.items);
          setUrgentTickerUpdatedAt(cached.updatedAt || "");
        }
      }
    } catch {
      // no-op: fallback to network fetch
    }

    if (activeSection !== "replenishment" || forecastViewMode !== "recommendations") {
      return;
    }

    let intervalId: number | undefined;
    const timeoutId = window.setTimeout(() => {
      loadUrgentTicker();
      intervalId = window.setInterval(loadUrgentTicker, 5 * 60 * 1000);
    }, 12000);

    return () => {
      window.clearTimeout(timeoutId);
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
  }, [activeSection, forecastViewMode, loadUrgentTicker]);

  // Available countries and warehouses for selection
  const warehouseOptions = [
    { code: "US", name: "US", type: "country", warehouses: ["PA", "LV", "IL"] },
    { code: "CA", name: "CA", type: "country", warehouses: ["TO"] },
    { code: "AU", name: "AU", type: "country", warehouses: ["AU"] },
    { code: "UK", name: "UK", type: "country", warehouses: ["GB", "FFL"] },
    { code: "CN", name: "CN", type: "country", warehouses: ["CN"] },
  ];

  // Function to get sales data for multiple SKUs with enhanced caching
  const getSalesDataForSkus = useCallback(async (skus: string[], warehouse: string): Promise<RawSalesRecord[]> => {
    const allSalesRecords: RawSalesRecord[] = [];

    // Safety check for skus array
    if (!skus || !Array.isArray(skus) || skus.length === 0) {
      console.warn(`⚠️ getSalesDataForSkus: Invalid or empty skus array:`, skus);
      return [];
    }

    // Fetch sales data for each SKU with caching
    const salesPromises = skus.map(async (sku) => {
      const cacheKey = CacheKeys.rawSales(sku, warehouse);

      return await salesDataCache.getOrSet(
        cacheKey,
        async () => {
          const response = await supplyChainService.getRawSkuSalesData(sku, warehouse);


          const salesRecords = response.raw_sales_data?.raw_sales_records || [];

          if (salesRecords.length > 0) {
            // Records without a date are dropped, since the run-rate math parses it.
            return salesRecords
              .filter((record) => record.date !== null)
              .map((record) => ({ ...record, date: record.date as string }));
          }
          console.warn(`No sales data found for ${sku} (${warehouse})`);
          return [];
        },
        30 * 60 * 1000 // 30 minutes TTL for sales data
      );
    });

    try {
      const allSkuSalesData = await Promise.all(salesPromises);
      allSkuSalesData.forEach(skuSalesData => {
        allSalesRecords.push(...skuSalesData);
      });


      if (allSalesRecords.length === 0) {
        console.warn(`No sales records found for SKUs: ${skus}`);
      }
    } catch (error) {
      console.warn(`Failed to get sales data for some SKUs:`, error);
    }

    return allSalesRecords;
  }, []);

  // Load raw data and calculate locally with enhanced caching
  const loadLocalStockData = useCallback(async () => {
    // Prevent multiple simultaneous requests
    if (loading || calculating) {
      return;
    }

    // prevent requests within 2 seconds of each other
    const now = Date.now();
    if (now - lastRequestTime < 2000) {
      return;
    }
    setLastRequestTime(now);

    try {
      setLoading(true);
      setCalculating(true);
      setError(null);
      setCalculationStatus("Loading inventory data...");

      // Prefer react-query cache, then fallback to direct API call.
      const backendData = (queryLocalData && !queryLocalError)
        ? queryLocalData
        : await supplyChainService.getLocalStockManagement(selectedWarehouse, {
          page: 1,
          pageSize: 200,
          includeSkuBreakdown: true,
          snapshotDate: selectedDate || undefined,
        });

      if (!backendData.products || backendData.products.length === 0) {
        setLocalStockData({
          warehouse: selectedWarehouse,
          total_products: 0,
          products: [],
          timestamp: new Date().toISOString(),
          data_source: "no_data",
          calculation_method: "backend_calculated"
        });
        setLoading(false);
        setCalculating(false);
        return;
      }
      const localStock: LocalStockData = {
        warehouse: selectedWarehouse,
        total_products: backendData.total_products || backendData.products.length,
        products: backendData.products,
        timestamp: backendData.timestamp || new Date().toISOString(),
        data_source: "backend_calculated",
        calculation_method: backendData.calculation_method || "backend_calculated"
      };
      setLocalStockData(localStock);
      setWarehouseData(prev => new Map(prev).set(`${selectedWarehouse}:${selectedDate}`, localStock));
    } catch (error) {
      console.error('Error loading backend-calculated stock data:', error);
      setError('Failed to load stock data');
    } finally {
      setLoading(false);
      setCalculating(false);
      setCalculationStatus("Ready");
    }
  }, [selectedWarehouse, selectedDate, lastRequestTime, loading, calculating, queryLocalData, queryLocalError]);

  // Load central stock data and calculate aggregations frontend-side
  const loadCentralStockData = useCallback(async (forceRefresh: boolean = false) => {
    // Prevent multiple simultaneous requests (skip check if force refresh)
    if (!forceRefresh && (loading || calculating)) {
      return;
    }

    // PREVENT EXCESSIVE CALLS: Add debounce check (skip if force refresh)
    const now = Date.now();
    if (!forceRefresh && now - ((window as any).lastCentralCall || 0) < 2000) {
      return;
    }
    (window as any).lastCentralCall = now;

    try {
      setLoading(true);
      setCalculating(true);
      setError(null);
      setCalculationStatus("Loading global inventory...");




      const rawCentralData = await supplyChainService.getCentralStockManagement({
        page: 1,
        pageSize: 200,
        compact: false,
        includeRawData: true,
      });

      // Process new countries_data format with frontend calculation
      if (!rawCentralData.countries_data || rawCentralData.countries_data.length === 0) {
        setCentralStockData({
          total_products: 0,
          products: [],
          summary: { reorder_flagged_count: 0, total_reorder_amount: 0 },
          timestamp: new Date().toISOString(),
          data_source: "no_data",
          calculation_method: "frontend_calculation"
        });
        return;
      }

      setCalculationStatus("Processing backend-calculated central stock...");

      // Load existing orders to calculate quantities in progress
      const existingOrders = JSON.parse(localStorage.getItem('supply_chain_orders') || '[]');

      // Calculate quantities in progress by product
      const quantitiesInProgress: { [productName: string]: number } = {};
      existingOrders.forEach((order: any) => {
        // Count orders that are in progress (allocated, manufacturing, pending_shipment, etc.)
        // but not yet delivered or cancelled
        if (['draft', 'confirmed', 'allocated', 'manufacturing', 'pending_shipment', 'in_transit', 'shipped'].includes(order.status)) {
          const productName = order.product_name || order.sku; // Handle both product_name and sku
          if (productName) {
            quantitiesInProgress[productName] = (quantitiesInProgress[productName] || 0) + (order.quantity || 0);
          }
        } else {
        }
      });

      Object.entries(quantitiesInProgress).forEach(([product, qty]) => {
      });

      // Consolidate backend-calculated rows by product across countries
      const productGroups: { [key: string]: any } = {};

      // Process each country's data
      for (const countryData of rawCentralData.countries_data) {
        const country = countryData.country;

        for (const rawProduct of countryData.products || []) {
          try {
            const productName = rawProduct.product_name;
            if (!productName) {
              continue;
            }
            const currentStock = rawProduct.current_stock || 0;
            const allocatedStock = rawProduct.allocated_stock || 0;
            const dailyRunRate = rawProduct.weighted_daily_run_rate || 0;
            const demandGrowthRate = rawProduct.demand_growth_rate || 0;
            const manufacturingDays = rawProduct.manufacturing_time_days || 25;
            const shippingDays = rawProduct.shipping_time_days || 45;
            const safetyStockDays = SAFETY_STOCK_DAYS;
            const timing = calculateReorderLogic(
              currentStock,
              allocatedStock,
              dailyRunRate,
              manufacturingDays,
              shippingDays,
              safetyStockDays,
              demandGrowthRate,
              0
            );

            // Group by product across all countries
            if (!productGroups[productName]) {
              productGroups[productName] = {
                product_name: productName,
                reorder_flagged: false,
                reorder_amount_required: 0,
                original_reorder_amount: 0, // Track original amount before deducting in-progress
                quantity_in_progress: quantitiesInProgress[productName] || 0, // Track in-progress quantity
                earliest_cutoff_date: null,
                warehouses: []
              };
            }

            // Add warehouse data with calculated metrics
            productGroups[productName].warehouses.push({
              warehouse: country,
              reorder_flagged: timing.reorder_flagged && timing.minimum_reorder_amount > 0,
              reorder_amount: timing.minimum_reorder_amount,
              cutoff_date: timing.order_cutoff_date,
              current_stock: currentStock,
              daily_run_rate: dailyRunRate,
              days_until_oos: timing.days_until_oos,
            });

            // Update product-level flags
            if (timing.reorder_flagged && timing.minimum_reorder_amount > 0) {
              productGroups[productName].reorder_flagged = true;
              productGroups[productName].original_reorder_amount += timing.minimum_reorder_amount;

              // Track earliest cutoff date
              if (timing.order_cutoff_date && timing.order_cutoff_date !== "N/A") {
                if (!productGroups[productName].earliest_cutoff_date ||
                  timing.order_cutoff_date < productGroups[productName].earliest_cutoff_date) {
                  productGroups[productName].earliest_cutoff_date = timing.order_cutoff_date;
                }
              }
            }

          } catch (error) {
            console.warn(`⚠️ Error calculating central data for ${rawProduct.product_name} in ${country}:`, error);
          }
        }
      }

      Object.values(productGroups).forEach((product: any) => {
        const productQuantityInProgress = product.quantity_in_progress || 0;
        const totalReorderAmountBeforeInProgress = product.warehouses.reduce(
          (sum: number, warehouse: any) => sum + (warehouse.reorder_amount || 0),
          0
        );
        const totalReorderAmountAfterInProgress = Math.max(0, totalReorderAmountBeforeInProgress - productQuantityInProgress);

        product.reorder_amount_required = totalReorderAmountAfterInProgress;
        product.reorder_flagged = totalReorderAmountAfterInProgress > 0;
      });



      // Convert to array
      const products = Object.values(productGroups);

      // Calculate summary
      const reorder_flagged_count = products.filter(p => p.reorder_flagged).length;
      const total_reorder_amount = products.reduce((sum, p) => sum + (p.reorder_amount_required || 0), 0);

      // FORCE REACT RE-RENDER: Create completely new objects with deep copy
      const newCentralStockData = {
        total_products: products.length,
        products: products.map(p => ({
          ...p,
          warehouses: p.warehouses.map((w: any) => ({ ...w })), // Deep copy warehouses
          _updateId: Date.now() // Force React to detect change
        })),
        summary: {
          reorder_flagged_count: reorder_flagged_count,
          total_reorder_amount: total_reorder_amount
        },
        timestamp: new Date().toISOString(),
        data_source: "central_stock_calculated",
        calculation_method: "frontend_calculation",
        _forceUpdate: Math.random() // Additional force update flag
      };

      //  Set new data with force refresh
      setCentralStockData(newCentralStockData);

    } catch (error) {
      console.error(' Error loading central stock data:', error);
      setError('Failed to load central stock data');
    } finally {
      setLoading(false);
      setCalculating(false);
      setCalculationStatus("Ready");
    }
  }, [loading, calculating, getSalesDataForSkus]);

  const loadOrdersData = useCallback(async () => {
    // Prevent multiple simultaneous requests
    if (loading) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get raw data from backend 
      const rawOrdersData = await supplyChainService.getOrdersToBePlaced();

      // Prefer backend-calculated orders when available.
      if (Array.isArray(rawOrdersData.orders)) {
        const backendOrders = rawOrdersData.orders;
        const urgentOrders = Array.isArray(rawOrdersData.urgent_orders)
          ? rawOrdersData.urgent_orders
          : backendOrders.filter((o: any) => o.priority === "urgent");

        setOrdersData({
          total_orders_needed: rawOrdersData.total_orders_needed ?? backendOrders.length,
          orders: backendOrders,
          urgent_orders: urgentOrders,
          timestamp: rawOrdersData.timestamp || new Date().toISOString(),
          calculation_method: rawOrdersData.calculation_method || "backend_calculated",
        });
        setEditedQuantities({});
        return;
      }

      //  frontend logic and consolidate by product
      const productOrders: { [key: string]: any } = {};

      if (rawOrdersData.countries_data) {
        for (const countryData of rawOrdersData.countries_data) {
          const country = countryData.country;

          for (const rawProduct of countryData.products || []) {
            try {
              // This legacy branch expects combined raw data, but central stock
              // supplies already-calculated products, so the shape is narrowed here.
              const legacyProduct = rawProduct as unknown as RawProductData & {
                sku_sales_data?: Array<{ raw_sales_records?: RawSalesRecord[] }>;
              };

              //  Aggregate all sales records from all SKUs before calculation
              const allSalesRecords: RawSalesRecord[] = [];

              for (const skuSales of legacyProduct.sku_sales_data || []) {
                const salesRecords = skuSales.raw_sales_records || [];
                allSalesRecords.push(...salesRecords);
              }


              // Use the same proven calculation logic as Local Stock Management
              const calculatedProduct = calculateProductMetrics(legacyProduct, allSalesRecords);

              // Check if this product needs reordering
              if (calculatedProduct.reorder_flagged && calculatedProduct.minimum_reorder_amount > 0) {
                const days_until_cutoff = Math.round((calculatedProduct.current_stock || 0) / Math.max(calculatedProduct.weighted_daily_run_rate || 0, 0.1));
                const priority = days_until_cutoff <= 7 ? "urgent" : "normal";

                const productName = calculatedProduct.product_name;

                //  Group orders by product across all countries
                if (!productOrders[productName]) {
                  productOrders[productName] = {
                    product_name: productName,
                    countries: [],
                    total_quantity_needed: 0,
                    order_cutoff_date: calculatedProduct.order_cutoff_date,
                    days_until_cutoff: days_until_cutoff,
                    priority: priority,
                    manufacturing_time: calculatedProduct.manufacturing_time_days,
                    total_estimated_cost: 0,
                    earliest_cutoff_days: days_until_cutoff,
                    reorder_flagged: true
                  };
                }

                // Add this country's order details
                productOrders[productName].countries.push({
                  country: country,
                  quantity_needed: calculatedProduct.minimum_reorder_amount,
                  current_stock: calculatedProduct.current_stock,
                  daily_run_rate: calculatedProduct.weighted_daily_run_rate,
                  days_until_cutoff: days_until_cutoff
                });

                // Aggregate totals with real unit costs
                const getProductCost = (productName: string): number => {
                  const costs: { [key: string]: number } = {
                    "NHH": 1.60,
                    "ARR Series": 1.40,
                    "ADRQ Series": 1.00,
                    "ARP Series": 1.00,
                    "RG Series": 1.00,
                    "Marketing Materials": 0.50
                  };
                  return costs[productName] || 1.00;
                };

                productOrders[productName].total_quantity_needed += calculatedProduct.minimum_reorder_amount;
                productOrders[productName].total_estimated_cost += (calculatedProduct.minimum_reorder_amount * getProductCost(productName));

                // Use most urgent priority and earliest cutoff
                if (days_until_cutoff < productOrders[productName].earliest_cutoff_days) {
                  productOrders[productName].earliest_cutoff_days = days_until_cutoff;
                  productOrders[productName].days_until_cutoff = days_until_cutoff;
                  productOrders[productName].order_cutoff_date = calculatedProduct.order_cutoff_date;
                }

                if (priority === "urgent") {
                  productOrders[productName].priority = "urgent";
                }

              }
            } catch (error) {
              console.warn(`⚠️ Error calculating order for ${rawProduct.product_name} in ${country}:`, error);
            }
          }
        }
      }

      //  Subtract quantities in progress from orders needed
      // Load existing orders from localStorage to check what's already in progress
      const existingOrders = JSON.parse(localStorage.getItem('supply_chain_orders') || '[]');

      // Calculate quantities in progress by product
      const quantitiesInProgress: { [productName: string]: number } = {};
      existingOrders.forEach((order: any) => {
        // Count orders that are in progress (allocated, manufacturing, pending_shipment, etc.)
        // but not yet delivered or cancelled
        if (['draft', 'confirmed', 'allocated', 'manufacturing', 'pending_shipment', 'in_transit', 'shipped'].includes(order.status)) {
          const productName = order.product_name || order.sku; // Handle both product_name and sku
          if (productName) {
            quantitiesInProgress[productName] = (quantitiesInProgress[productName] || 0) + (order.quantity || 0);
          }
        }
      });


      // Convert to array and prepare final orders using correct formula
      const ordersNeeded = Object.values(productOrders)
        .map((order: any) => {
          const quantityInProgress = quantitiesInProgress[order.product_name] || 0;

          //  Recalculate using Target Stock - Current - Allocated - In Progress
          let totalQuantityNeeded = 0;
          let totalCurrentStock = 0;
          let totalDailyRunRate = 0;
          let totalTargetStock = 0;

          // First, calculate totals across all countries WITHOUT deducting quantityInProgress
          order.countries.forEach((country: any) => {
            const dailyRunRate = country.daily_run_rate || 0;
            const currentStock = country.current_stock || 0;
            const allocatedStock = 0; // Assume 0 for orders calculation

            if (dailyRunRate > 0) {
              const targetStock = Math.round(104 * dailyRunRate); // 104 days target
              totalTargetStock += targetStock;
              // Don't subtract quantityInProgress here - it's a GLOBAL value, not per-country
              const countryQuantityNeeded = Math.max(0, targetStock - currentStock - allocatedStock);
              totalQuantityNeeded += countryQuantityNeeded;
            }

            totalCurrentStock += currentStock;
            totalDailyRunRate += dailyRunRate;
          });

          // Subtract quantityInProgress ONCE from the total, not per-country
          const totalQuantityNeededBeforeInProgress = totalQuantityNeeded;
          totalQuantityNeeded = Math.max(0, totalQuantityNeeded - quantityInProgress);



          //  Round to prevent small calculation differences and filter out tiny remainders
          const roundedQuantityNeeded = Math.round(totalQuantityNeeded);

          return {
            product_name: order.product_name,
            country: order.countries.map((c: any) => c.country).join(', '), // Show all countries
            quantity_needed: roundedQuantityNeeded, //Use correct formula result (always fresh calculated value)
            quantity_in_progress: quantityInProgress, // Track quantity in progress
            order_cutoff_date: order.order_cutoff_date,
            days_until_cutoff: order.days_until_cutoff,
            reorder_flagged: roundedQuantityNeeded > 0, // Only flag if rounded quantity still needed
            priority: order.priority,
            manufacturing_time: order.manufacturing_time,
            estimated_cost: roundedQuantityNeeded * (order.total_estimated_cost / Math.max(order.total_quantity_needed, 1)), // Adjust cost proportionally
            current_stock: totalCurrentStock,
            daily_run_rate: totalDailyRunRate,
            countries_details: order.countries // Keep detailed breakdown
          };
        })
        .filter((order: any) => order.quantity_needed > 0); // Only show orders still needed

      const urgentOrders = ordersNeeded.filter(order => order.priority === "urgent");

      const calculatedOrdersData: OrdersData = {
        total_orders_needed: ordersNeeded.length,
        orders: ordersNeeded,
        urgent_orders: urgentOrders,
        timestamp: new Date().toISOString(),
        calculation_method: "frontend_calculation"
      };



      setOrdersData(calculatedOrdersData);
      setEditedQuantities({}); // Clear all edits when data refreshes to show calculated values

    } catch (error) {
      console.error(' Error processing orders data:', error);
      setError('Failed to process orders data');
      // Set ordersData to empty object to prevent fallback values
      setOrdersData({
        total_orders_needed: 0,
        orders: [],
        urgent_orders: [],
        timestamp: new Date().toISOString(),
        calculation_method: "frontend_calculation"
      });
    } finally {
      setLoading(false);
    }
  }, [loading]);

  // Only trigger on section or warehouse changes - avoid reloading cached data

  useEffect(() => {
    // Skip if already loading to prevent duplicate requests
    if (loading || calculating) {
      return;
    }

    if (activeSection === "local") {
      // Check if React Query already has data for this warehouse
      if (queryLocalData && !queryLocalError) {
        console.log(`React Query has data for ${selectedWarehouse}, will use in loadLocalStockData`);
      }

      // Check if we have data for this warehouse+date in memory
      const cacheKey = `${selectedWarehouse}:${selectedDate}`;
      const existingData = warehouseData.get(cacheKey);
      if (existingData) {
        setLocalStockData(existingData);
      } else {
        loadLocalStockData();
      }
    } else if (activeSection === "central") {
      if (!centralStockData) {
        // Only load if no cached data
        loadCentralStockData();
      } else {
        console.log(' Using cached central stock data');
      }
    } else if (activeSection === "orders") {
      if (!ordersData) {
        // Only load if no cached data
        console.log(' Loading orders data (no cache)');
        loadOrdersData();
      } else {
        console.log(' Using cached orders data');
      }
    }


  }, [activeSection, selectedWarehouse, selectedDate]); // re-run when warehouse or date changes

  const handleClearCache = async () => {
    setIsClearingCache(true);
    try {
      // TanStack Query: Use mutation for comprehensive cache clearing
      await clearCacheMutation.mutateAsync();

      // Clear traditional frontend caches 
      rawDataCache.clear();
      calculationCache.clear();
      salesDataCache.clear();

      // Clear TanStack Query specific cache patterns
      clearSpecificCache(['raw-data-combined', 'local-stock-management', 'external-inventory']);


      // Clear all loaded data and reset tracking
      setLocalStockData(null);
      setCentralStockData(null);
      setOrdersData(null);
      setWarehouseData(new Map());

      // After clearing the cache, reload only the current section's data
      if (activeSection === "local") {
        await loadLocalStockData();
      } else if (activeSection === "central") {
        await loadCentralStockData();
      } else if (activeSection === "orders") {
        await loadOrdersData();
      }
      // Note: order-management section doesn't need cache clearing as it's self-contained
    } catch (error) {
      console.error("Failed to clear cache:", error);
      setError("Failed to clear cache.");
    } finally {
      setIsClearingCache(false);
    }
  };

  //  Sync BigQuery inventory data with TanStack Query
  const handleSyncBigQuery = async () => {
    setIsSyncingBigQuery(true);
    try {
      setCalculationStatus("Refreshing BigQuery inventory...");

      // Clear backend cache to force a fresh fetch from BigQuery (ShipFusion etc.)
      await clearCacheMutation.mutateAsync();

      // RELOAD UI DATA after sync to show updated inventory
      setCalculationStatus("Refreshing display...");

      // Clear traditional frontend caches
      rawDataCache.clear();
      calculationCache.clear();
      salesDataCache.clear();

      refetchLocalData();

      // Reload the current section's data to show updated inventory
      if (activeSection === "local") {
        await loadLocalStockData();
      } else if (activeSection === "central") {
        await loadCentralStockData();
      } else if (activeSection === "orders") {
        await loadOrdersData();
      }
      // Note: order-management section doesn't need BigQuery sync as it's self-contained

      setCalculationStatus("Ready");

    } catch (error) {
      console.error("❌ Failed to sync BigQuery inventory:", error);
      setError("Failed to sync BigQuery inventory.");
      setCalculationStatus("Ready");
    } finally {
      setIsSyncingBigQuery(false);
    }
  };

  // Toggle product expansion and fetch SKU data
  const toggleProductExpansion = async (productName: string, skus: string[] = []) => {

    const isExpanded = expandedProducts.has(productName);

    if (isExpanded) {

      setExpandedProducts(prev => {
        const newSet = new Set(prev);
        newSet.delete(productName);
        return newSet;
      });
    } else {
      // Expand - add to expanded set and fetch SKU data
      setExpandedProducts(prev => new Set(prev).add(productName));

      // Fetch SKU data if not already cached
      const cacheKey = `${productName}-${selectedWarehouse}`;
      if (!skuData.has(cacheKey)) {
        try {
          // Set loading state for this specific product
          setLoadingSkuData(prev => new Set(prev).add(productName));
          setCalculationStatus(`Loading SKU details for ${productName}...`);

          //  Use sku_breakdown data directly from backend instead of complex recalculation
          // Find this product in the main stock data (which has sku_breakdown)
          const productData = localStockData?.products?.find((p: any) => p.product_name === productName);

          // Use sku_breakdown data directly from backend
          if (productData?.sku_breakdown && productData.sku_breakdown.length > 0) {


            // Transform backend sku_breakdown data to frontend format
            const isOnlySku = productData.sku_breakdown.length === 1;
            const processedSkuData = await Promise.all(productData.sku_breakdown.map(async (skuData: any) => {

              // When this is the only physical SKU, all product demand depletes from it —
              // use product-level DRR so OOS date, forecast, and rates stay consistent.
              const drr7 = isOnlySku ? (productData.daily_run_rate_7 || skuData.daily_run_rate_7 || 0) : (skuData.daily_run_rate_7 || 0);
              const drr14 = isOnlySku ? (productData.daily_run_rate_14 || skuData.daily_run_rate_14 || 0) : (skuData.daily_run_rate_14 || 0);
              const drr30 = isOnlySku ? (productData.daily_run_rate_30 || skuData.daily_run_rate_30 || 0) : (skuData.daily_run_rate_30 || 0);
              const weightedDRR = isOnlySku ? (productData.weighted_daily_run_rate || skuData.weighted_daily_run_rate || 0) : (skuData.weighted_daily_run_rate || 0);
              const mfgDays = isOnlySku ? (productData.manufacturing_time_days || skuData.manufacturing_time_days || 25) : (skuData.manufacturing_time_days || 25);
              const currentStock = skuData.current_stock || 0;
              const allocatedStock = skuData.allocated_stock || 0;
              const oosDate = calculateOOSDate(
                Math.max(0, currentStock - allocatedStock),
                weightedDRR,
                0
              );
              const reorderTiming = calculateReorderLogic(
                currentStock,
                allocatedStock,
                weightedDRR,
                25,
                45,
                14,
                0,
                0
              );

              //  Return backend data directly with minimal transformation
              let inStockRate90: number | null = null;
              let inStockRate180: number | null = null;
              try {
                const history = await supplyChainService.getHistoricalStockData(
                  skuData.sku,
                  selectedWarehouse,
                  180
                );
                if (typeof history?.in_stock_rate_90 === "number") {
                  inStockRate90 = history.in_stock_rate_90;
                }
                if (typeof history?.in_stock_rate_180 === "number") {
                  inStockRate180 = history.in_stock_rate_180;
                }
              } catch {
                // Keep null when history is unavailable for this SKU
              }

              const skuDGR = skuData.demand_growth_rate || 0;
              // Use the same weightedDRR as oosDate/reorderTiming above (already
              // prefers product-level DRR when isOnlySku) so this row stays consistent.
              const skuDRR = weightedDRR;
              const skuForecast90 = skuData.forecast_stock_90_days != null
                ? skuData.forecast_stock_90_days
                : calculateForecastStock(currentStock, skuDRR, skuDGR);

              const skuResult = {
                sku: skuData.sku,
                current_stock: currentStock,
                allocated_stock: allocatedStock,
                daily_run_rate_7: drr7,
                daily_run_rate_14: drr14,
                daily_run_rate_30: drr30,
                weighted_daily_run_rate: weightedDRR,
                demand_growth_rate: skuDGR,
                forecast_stock_90_days: skuForecast90,
                manufacturing_time_days: mfgDays,
                oos_date: isOnlySku
                  ? (productData?.oos_date || null)
                  : (oosDate !== "Unknown" && oosDate !== "No consumption" ? oosDate : null),
                reorder_flagged: reorderTiming.reorder_flagged,
                minimum_reorder_amount: reorderTiming.minimum_reorder_amount,
                order_cutoff_date: reorderTiming.order_cutoff_date !== "N/A" ? reorderTiming.order_cutoff_date : null,
                days_until_cutoff: reorderTiming.days_until_cutoff,
                in_stock_rate_90: inStockRate90,
                in_stock_rate_180: inStockRate180,
                platforms: [skuData.platform].filter(Boolean),
                record_count: skuData.record_count || 1,
                warehouse_breakdown: skuData.warehouse_breakdown || [] // ✅ PRESERVE WAREHOUSE BREAKDOWN
              };


              return skuResult;
            }));

            // Cache the processed SKU data
            setSkuData(prev => new Map(prev).set(cacheKey, processedSkuData));
            setCalculationStatus("Ready");
          } else {
            // No sku_breakdown data available
            setSkuData(prev => new Map(prev).set(cacheKey, [])); // Empty array
            setCalculationStatus("Ready");
          }

        } catch (error) {
          console.error('Error processing SKU data:', error);
          setSkuData(prev => new Map(prev).set(cacheKey, [])); // Empty array on error
          setCalculationStatus("Ready");
        } finally {
          // Clear loading state for this product
          setLoadingSkuData(prev => {
            const newSet = new Set(prev);
            newSet.delete(productName);
            return newSet;
          });
        }
      }
    }
  };

  // Handle order confirmation from orders to be placed 
  const handleConfirmOrder = async (order: any) => {
    try {

      // Get the primary SKU for this product (use the first SKU from the mapping)
      const productSkus = PRODUCT_SKU_MAPPING[order.product_name] || [];
      if (productSkus.length === 0) {
        showToast('error', 'SKU Not Found', `No SKU mapping found for product: ${order.product_name}`);
        return;
      }

      // Use the first SKU as the primary SKU for the order
      const primarySku = productSkus[0];

      // Calculate price per unit (use a default or get from order data)
      const pricePerUnit = order.estimated_cost / order.quantity_needed || 1.0;

      //  Use exact quantity from the order to ensure consistency
      const orderQuantity = order.quantity_needed; // This is the exact quantity 


      // Create the order data 
      const orderNumber = `ORD-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${primarySku}-${orderQuantity}`;
      const totalCost = orderQuantity * pricePerUnit;

      const newOrder = {
        order_id: Date.now(), // Use timestamp as ID
        order_number: orderNumber,
        sku: primarySku,
        quantity: orderQuantity,
        price_per_unit: pricePerUnit,
        total_cost: totalCost,
        date_ordered: new Date().toISOString().split('T')[0],
        status: 'draft',
        manufacturing_complete: false,
        final_allocation_locked: false,
        created_at: new Date().toISOString(),
        product_name: order.product_name,
        estimated_cost: order.estimated_cost,
        countries_details: order.countries_details || [], // Include country breakdown for allocation
        // Store original order data for reference
        original_order: order
      };

      // Store in localStorage for persistence 
      const existingOrders = JSON.parse(localStorage.getItem('supply_chain_orders') || '[]');
      existingOrders.push(newOrder);
      localStorage.setItem('supply_chain_orders', JSON.stringify(existingOrders));

      //  Trigger custom event for same-tab localStorage changes
      window.dispatchEvent(new CustomEvent('localStorageChange', {
        detail: { key: 'supply_chain_orders', newValue: JSON.stringify(existingOrders) }
      }));


      // Clear caches and refresh data immediately BEFORE showing alert
      calculationCache.clear(); // Clear calculation cache to force fresh calculations

      //  Refresh BOTH Orders AND Central Stock Management
      await loadOrdersData();

      await loadCentralStockData(true); //  Skip debounce to immediately update after order confirmation


      // Show success message AFTER refresh completes
      showToast('success', 'Order Created Successfully', `Order ${orderNumber} for ${orderQuantity.toLocaleString()} units of ${primarySku} ($${totalCost.toLocaleString()})`);


    } catch (error) {
      console.error('❌ Error confirming order:', error);
      showToast('error', 'Order Failed', `Error confirming order: ${error}`);
    }
  };


  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-white border-r border-gray-200 flex flex-col transition-all duration-300 ease-in-out`}>
        {/* Sidebar Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200">
          {sidebarOpen && (
            <h1 className="text-lg font-bold text-gray-900">Supply Chain</h1>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
          <button
            onClick={() => navigate("/local-stock")}
            className={`w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${activeSection === "local"
              ? "bg-blue-50 text-blue-700"
              : "text-gray-700 hover:bg-gray-50"
              }`}
          >
            <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            {sidebarOpen && <span>Local Stock</span>}
          </button>

          <button
            onClick={() => {
              navigate("/central-stock");
              // Only load if no data exists (don't force refresh on every click)
              if (!centralStockData) {
                loadCentralStockData();
              } else {
                console.log(' Using cached Central Stock data');
              }
            }}
            className={`w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${activeSection === "central"
              ? "bg-blue-50 text-blue-700"
              : "text-gray-700 hover:bg-gray-50"
              }`}
          >
            <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            {sidebarOpen && <span>Central Stock</span>}
          </button>

          {/* Order Management - Combines "Orders to Place" + tracking in progress */}
          <button
            onClick={() => navigate("/order-management")}
            className={`w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${activeSection === "order-management" || activeSection === "orders"
              ? "bg-blue-50 text-blue-700"
              : "text-gray-700 hover:bg-gray-50"
              }`}
          >
            <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
            {sidebarOpen && <span>Order Management</span>}
          </button>

          {/* Inventory Planning - Merged Replenishment & Forecast */}
          <button
            onClick={() => navigate("/replenishment")}
            className={`w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${activeSection === "replenishment"
              ? "bg-blue-50 text-blue-700"
              : "text-gray-700 hover:bg-gray-50"
              }`}
          >
            <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            {sidebarOpen && <span>Forecast</span>}
          </button>

          <button
            onClick={() => navigate("/shipping-analytics")}
            className={`w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${activeSection === "shipping-analytics"
              ? "bg-blue-50 text-blue-700"
              : "text-gray-700 hover:bg-gray-50"
              }`}
          >
            <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
            </svg>
            {sidebarOpen && <span>Shipping Analytics</span>}
          </button>

          <button
            onClick={() => navigate("/product-analytics")}
            className={`w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${activeSection === "product-analytics"
              ? "bg-blue-50 text-blue-700"
              : "text-gray-700 hover:bg-gray-50"
              }`}
          >
            <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
            </svg>
            {sidebarOpen && <span>Product Analytics</span>}
          </button>

          <button
            onClick={() => navigate("/unit-economics/global-settings")}
            className={`w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${activeSection === "unit-economics"
              ? "bg-blue-50 text-blue-700"
              : "text-gray-700 hover:bg-gray-50"
              }`}
          >
            <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {sidebarOpen && <span>Unit Economics</span>}
          </button>
        </nav>

        {/* Sidebar Footer */}
        <div className="border-t border-gray-200 px-2 py-3 space-y-2">
          <div
            className={`w-full ${sidebarOpen ? 'px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-200' : 'flex justify-center py-1'}`}
            title={loggedInDisplayName}
          >
            <div className={`flex items-center ${sidebarOpen ? 'gap-2.5' : 'justify-center'}`}>
              {loggedInAvatarUrl ? (
                <img
                  src={loggedInAvatarUrl}
                  alt="User avatar"
                  className="w-8 h-8 rounded-full object-cover border border-gray-200"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold flex items-center justify-center border border-blue-200">
                  {loggedInInitial}
                </div>
              )}
              {sidebarOpen && (
                <div className="min-w-0">
                  <div className="text-xs text-gray-500">Logged in as</div>
                  <div className="text-sm font-medium text-gray-900 truncate">{loggedInDisplayName}</div>
                </div>
              )}
            </div>
          </div>

          <button
            onClick={handleSyncBigQuery}
            className={`w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed ${!sidebarOpen && 'justify-center'}`}
            disabled={calculating || isClearingCache || isSyncingBigQuery}
          >
            {isSyncingBigQuery ? (
              <svg className="animate-spin w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            )}
            {sidebarOpen && <span className="ml-3">{isSyncingBigQuery ? "Syncing..." : "Sync BigQuery"}</span>}
          </button>

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className={`w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors text-red-600 hover:bg-red-50 ${!sidebarOpen && 'justify-center'}`}
          >
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            {sidebarOpen && <span className="ml-3">Logout</span>}
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Scrollable Content */}
        <div className="flex-1 overflow-auto bg-gray-50 p-8">
          {/* Warehouse Selector for Local Stock Management */}
          {activeSection === "local" && (
            <div className="mb-8 bg-white rounded-lg border border-gray-200 p-4 flex items-center justify-between shadow-sm">
              <div className="flex items-center space-x-3 flex-1">
                <div className="bg-blue-50 rounded-lg p-2">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </div>
                <div className="relative" ref={warehouseDropdownRef}>
                  <label className="block text-xs font-medium text-gray-500 mb-0.5">
                    Warehouse Location
                  </label>
                  <button
                    onClick={() => setWarehouseDropdownOpen(!warehouseDropdownOpen)}
                    className="inline-flex items-center space-x-2 bg-transparent border-none text-base font-semibold text-gray-900 focus:outline-none cursor-pointer hover:text-blue-600 transition-colors"
                  >
                    <span>{warehouseOptions.find(w => w.code === selectedWarehouse)?.name || selectedWarehouse}</span>
                    <svg className={`w-5 h-5 text-gray-400 transition-transform ${warehouseDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Custom Dropdown Menu */}
                  {warehouseDropdownOpen && (
                    <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg border border-gray-200 shadow-xl z-50 overflow-hidden">
                      <div className="py-1">
                        {warehouseOptions.filter(option => option.type === "country").map((option) => (
                          <button
                            key={option.code}
                            onClick={() => {
                              setSelectedWarehouse(option.code);
                              setWarehouseDropdownOpen(false);
                            }}
                            className={`w-full text-left px-4 py-2.5 transition-colors flex items-center justify-between ${selectedWarehouse === option.code
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-700 hover:bg-gray-100'
                              }`}
                          >
                            <span className="font-medium text-sm">{option.name}</span>
                            {selectedWarehouse === option.code && (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

              </div>
              <div className="flex items-center space-x-2 text-sm">
                <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full font-medium">
                  {warehouseOptions.find(w => w.code === selectedWarehouse)?.type === "country" ? "Country View" : "Single Location"}
                </span>
              </div>
            </div>
          )}

          {/* Section Header with Actions */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {activeSection === "local" && "Local Stock Management"}
                {activeSection === "central" && "Central Stock Management"}
                {activeSection === "orders" && "Order Management"}
                {activeSection === "order-management" && "Order Management"}
                {activeSection === "replenishment" && "Forecast Analysis"}
                {activeSection === "shipping-analytics" && "Last Mile Shipping Analytics"}
                {activeSection === "product-analytics" && "Product Mix Trends"}
                {activeSection === "product-cogs" && "Product COGS"}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {activeSection === "local" && "Real-time inventory analysis and stock monitoring"}
                {activeSection === "central" && "Overview of all products across all locations"}
                {activeSection === "orders" && "Track urgent orders and manage orders in progress"}
                {activeSection === "order-management" && "Track urgent orders and manage orders in progress"}
                {activeSection === "replenishment" && "12-month forecast and replenishment recommendations"}
                {activeSection === "shipping-analytics" && "Comprehensive shipping cost analysis and order tracking"}
                {activeSection === "product-analytics" && "Track product distribution trends for better inventory planning"}
                {activeSection === "product-cogs" && "Configure Cost of Goods Sold per product for CM$ calculations"}
                {activeSection === "forecast" && "Projected reorder schedule based on inventory levels and demand"}
              </p>
              {/* Calculation Status */}
              {(calculating || calculationStatus !== "Ready") && (
                <div className="flex items-center mt-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse mr-2"></div>
                  <span className="text-sm text-blue-600 font-medium">{calculationStatus}</span>
                </div>
              )}
            </div>
            <div className="flex items-center space-x-3">
              {/* Forecast View Switcher - Only show for Replenishment section */}
              {activeSection === "replenishment" && (
                <div className="flex bg-white p-1 rounded-lg border border-gray-200 shadow-sm">
                  <button
                    onClick={() => setForecastViewMode('forecast')}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${forecastViewMode === 'forecast'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-50'
                      }`}
                  >
                    12-Month Forecast
                  </button>
                  <button
                    onClick={() => setForecastViewMode('recommendations')}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${forecastViewMode === 'recommendations'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-50'
                      }`}
                  >
                    Recommendations
                  </button>
                </div>
              )}
              {/* Refresh Orders Button - Only show for Orders section */}
              {activeSection === "orders" && (
                <button
                  onClick={() => loadOrdersData()}
                  className="inline-flex items-center px-4 py-2.5 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  disabled={loading || calculating}
                >
                  {loading ? (
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  )}
                  {loading ? "Refreshing..." : "Refresh Orders"}
                </button>
              )}
            </div>
          </div>

          <div
            className="mb-4 bg-white rounded-lg border border-gray-200 shadow-sm px-4 py-2 cursor-pointer hover:border-blue-300 transition-colors"
            onClick={() => navigate("/replenishment")}
            title="Open Forecast for full reorder details"
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-red-600 uppercase tracking-wider">Live Urgent Reorder Alerts</span>
              <span className="text-xs text-gray-500">Updated {urgentTickerUpdatedAt || "now"}</span>
            </div>
            <div className="overflow-hidden whitespace-nowrap">
              <div
                className="ticker-track inline-flex items-center gap-10 min-w-max"
              >
                {urgentTickerItems.map((item) => (
                  <span key={item.key} className="text-sm text-gray-800 font-medium">
                    {item.text}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <style>{`
            .ticker-track {
              animation: tickerScroll 55s linear infinite;
              will-change: transform;
            }
            .ticker-track:hover {
              animation-play-state: paused;
            }
            @keyframes tickerScroll {
              0% { transform: translateX(100%); }
              100% { transform: translateX(-100%); }
            }
          `}</style>

          {/* Content Area */}
          {(loading || calculating) ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">

              <div className="p-6">
                {/* Header Skeleton */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
                    <div className="space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
                      <div className="h-3 bg-gray-100 rounded w-24 animate-pulse"></div>
                    </div>
                  </div>
                  <div className="h-8 bg-gray-200 rounded w-20 animate-pulse"></div>
                </div>

                {/* Table Header Skeleton */}
                <div className="grid grid-cols-7 gap-4 mb-4">
                  {[...Array(7)].map((_, i) => (
                    <div key={i} className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  ))}
                </div>

                {/* Table Rows Skeleton */}
                <div className="space-y-3">
                  {[...Array(5)].map((_, rowIndex) => (
                    <div key={rowIndex} className="grid grid-cols-7 gap-4 py-3">
                      {/* Product Name Column */}
                      <div className="space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
                        <div className="h-3 bg-gray-100 rounded w-16 animate-pulse"></div>
                      </div>

                      {/* Other Columns */}
                      {[...Array(6)].map((_, colIndex) => (
                        <div key={colIndex} className="h-4 bg-gray-200 rounded animate-pulse"></div>
                      ))}
                    </div>
                  ))}
                </div>

                {/* Subtle Loading Indicator */}
                <div className="flex items-center justify-center mt-8 py-4">
                  <div className="text-center">
                    <div className="flex items-center justify-center space-x-2 text-gray-500">
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                      <span className="ml-3 text-sm">Loading inventory...</span>
                    </div>
                    <p className="text-gray-400 text-xs mt-2">This may take up to 2 minutes on first load</p>
                  </div>
                </div>
              </div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">Error: {error}</p>
            </div>
          ) : (
            <>
              {/* Local Stock Management - Loading State (rare edge case) */}
              {activeSection === "local" && !localStockData && !loading && !calculating && (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
                    <p className="text-gray-600">Loading inventory data...</p>
                  </div>
                </div>
              )}

              {/* Local Stock Management */}
              {activeSection === "local" && (loading || calculating || localStockData) && (
                <>
                  {/* Mobile Card Layout */}
                  <div className="block sm:hidden space-y-4">
                    {localStockData?.products?.map((product, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4 space-y-3">
                        <div className="flex justify-between items-center border-b pb-2">
                          <h3 className="font-semibold text-gray-900">{product.product_name}</h3>
                          <span className="text-sm text-gray-500">{product.sku_count} SKUs</span>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-gray-500">Stock:</span>
                            <span className="ml-2 font-medium">{product.current_stock?.toLocaleString() || '0'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">DRR:</span>
                            <span className="ml-2 font-medium">{product.weighted_daily_run_rate?.toFixed(2) || '0.00'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Forecast:</span>
                            <span className="ml-2 font-medium">{product.forecast_stock_90_days?.toLocaleString() || '0'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Status:</span>
                            <span className={`ml-2 px-2 py-1 rounded text-xs font-medium ${product.reorder_flagged
                              ? "bg-red-100 text-red-800"
                              : "bg-green-100 text-green-800"
                              }`}>
                              {product.reorder_flagged ? "REORDER NOW" : "OK"}
                            </span>
                          </div>
                        </div>
                        <div className="pt-2 border-t">
                          <div className="flex justify-between">
                            <span className="text-gray-500 text-sm">Reorder Amount:</span>
                            <span className="font-medium">
                              {product.minimum_reorder_amount?.toLocaleString() || '0'}
                              <span className="ml-1 text-xs text-gray-400" title="Target: 104 days (3.5 months) of stock">ⓘ</span>
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Filter bar: Last Updated + Date picker */}
                  <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200 flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center text-sm">
                      <svg className="w-4 h-4 text-blue-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-blue-800">
                        <strong>Last Updated:</strong>{" "}
                        {localStockData?.timestamp ? formatDate(localStockData.timestamp) : "—"}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-blue-800">View inventory on:</label>
                      <input
                        type="date"
                        value={inputDate}
                        max={new Date().toISOString().split("T")[0]}
                        onChange={(e) => setInputDate(e.target.value)}
                        className="text-sm border border-blue-300 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                      />
                      {selectedDate !== new Date().toISOString().split("T")[0] && (
                        <>
                          <button
                            onClick={() => { const today = new Date().toISOString().split("T")[0]; setInputDate(today); setSelectedDate(today); }}
                            className="text-xs px-2 py-1 bg-amber-100 text-amber-700 border border-amber-300 rounded-md hover:bg-amber-200 transition-colors"
                          >
                            Reset to today
                          </button>
                          <span className="text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-1 rounded-md border border-amber-300">
                            Historical view
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Desktop Table Layout */}
                  <div className="mb-3 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center text-sm text-blue-700">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span><strong>Tip:</strong> Click on any product row to expand and view SKU breakdown with in-stock rates and stock history graphs. Scroll right to see all columns.</span>
                    </div>
                  </div>
                  <div className="hidden sm:block overflow-x-auto bg-white border border-gray-200 rounded-lg shadow-sm">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="sticky left-0 z-20 bg-gray-50 px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                            Product Name
                          </th>
                          <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Current Stock
                          </th>
                          <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            DRR 7d
                          </th>
                          <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            DRR 14d
                          </th>
                          <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            DRR 30d
                          </th>
                          <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Weighted DRR
                          </th>
                          <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            ADDG
                          </th>
                          <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Stock Allocated
                          </th>
                          <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            90-Day Forecast
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            OOS Date
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Reorder Status
                          </th>
                          <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Reorder Amount
                            <span className="ml-1 text-gray-400 cursor-help" title="Target: 104 days (3.5 months) of stock">ⓘ</span>
                          </th>
                          <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            MT (days)
                          </th>
                          <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            ST (days)
                          </th>
                          <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            In-Stock 90d
                            <span className="ml-1 text-gray-400 cursor-help" title="% of days with stock in last 90 days">ⓘ</span>
                          </th>
                          <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            In-Stock 180d
                            <span className="ml-1 text-gray-400 cursor-help" title="% of days with stock in last 180 days">ⓘ</span>
                          </th>
                          <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            OOS Rate (180d)
                            <span className="ml-1 text-gray-400 cursor-help" title="% of days out of stock in last 180 days">ⓘ</span>
                          </th>
                          <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Graph
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {localStockData?.products.map((product, index) => {
                          // Unmapped SKUs come back with no product name, so rows fall
                          // back to a per-row identity instead of sharing one key.
                          const productName = product.product_name ?? `unnamed-${index}`;
                          const isExpanded = expandedProducts.has(productName);
                          const cacheKey = `${productName}-${selectedWarehouse}`;
                          const isSkuHistoryLoading = loadingSkuData.has(productName);

                          // CALCULATE SKU BREAKDOWN ON-DEMAND from raw inventory data
                          const rawProductData = product.product_name
                            ? queryLocalData?.products?.find((p: any) => p.product_name === product.product_name)
                            : undefined;
                          const skuDetails: any[] = [];
                          const isRawDataLoading = queryLocalLoading;
                          const hasRawDataError = !!queryLocalError;

                          // If expanded and cached SKU data is available, use it first
                          const cachedSkuData = skuData.get(cacheKey);
                          if (isExpanded && Array.isArray(cachedSkuData) && cachedSkuData.length > 0) {
                            skuDetails.push(...cachedSkuData);
                          }
                          // Otherwise, build SKU metrics from current payload
                          else if (isExpanded && rawProductData) {
                            // Initialize ALL SKUs for this product (even if 0 stock), excluding China-only SKUs for non-CN warehouses
                            const chinaOnlySkus = ["ADRQ-2024003", "ARR-PB", "RG-Brain", "RG-Gut", "RG-Heart", "brochure", "ARP-NK", "ARP-14D-L", "ARP-CS-BS", "ARP-CS-JP", "ARP-K1"];
                            const allProductSkus = PRODUCT_SKU_MAPPING[productName] || [];

                            // Filter SKUs based on warehouse
                            const productSkus = selectedWarehouse === 'CN'
                              ? allProductSkus
                              : allProductSkus.filter((sku: string) => !chinaOnlySkus.includes(sku));

                            const skuGroups: { [key: string]: any[] } = {};

                            // Initialize all expected SKUs with empty arrays
                            productSkus.forEach((sku: string) => {
                              skuGroups[sku] = [];
                            });

                            // Extract backend computed SKU data directly
                            if (product.sku_breakdown && product.sku_breakdown.length > 0) {
                              // If backend already computed it, just use it
                              product.sku_breakdown.forEach((skuData: any) => {
                                const skuDrr = skuData.weighted_daily_run_rate || 0;
                                const dgr = skuData.demand_growth_rate || 0;
                                const backendForecast = skuData.forecast_stock_90_days;
                                const forecast90 = backendForecast != null
                                  ? backendForecast
                                  : calculateForecastStock(skuData.current_stock || 0, skuDrr, dgr);
                                skuDetails.push({
                                  ...skuData,
                                  daily_run_rate_7: skuData.daily_run_rate_7,
                                  daily_run_rate_14: skuData.daily_run_rate_14,
                                  daily_run_rate_30: skuData.daily_run_rate_30,
                                  weighted_daily_run_rate: skuDrr,
                                  manufacturing_time_days: skuData.manufacturing_time_days,
                                  oos_date: skuData.oos_date,
                                  forecast_stock_90_days: forecast90,
                                  reorder_flagged: skuData.minimum_reorder_amount > 0,
                                  in_stock_rate_90: skuData.in_stock_rate_90 ?? null,
                                  in_stock_rate_180: skuData.in_stock_rate_180 ?? null,
                                  platforms: [skuData.platform].filter(Boolean),
                                  record_count: skuData.record_count || 1,
                                });
                              });
                            } else {
                              // Fallback if no backend sku_breakdown
                              const totalProductStock = product.current_stock || 1; // Avoid division by zero
                              Object.entries(skuGroups).forEach(([sku, records]) => {
                                const totalStock = records.reduce((sum: number, r: any) => sum + (r.on_hand_quantity || 0), 0);
                                const allocatedStock = records.reduce((sum: number, r: any) => sum + (r.allocated_quantity || 0), 0);
                                const platforms = Array.from(new Set(records.map((r: any) => r.platform).filter(Boolean)));

                                const stockProportion = totalProductStock > 0 ? totalStock / totalProductStock : 0;

                                const warehouse_breakdown: { warehouse: string, stock: number }[] = [];
                                if (records.length > 1) {
                                  records.forEach((rec: any) => {
                                    warehouse_breakdown.push({
                                      warehouse: rec.warehouse_name || rec.platform || 'Unknown',
                                      stock: rec.on_hand_quantity || 0
                                    });
                                  });
                                }

                                skuDetails.push({
                                  sku,
                                  current_stock: totalStock,
                                  allocated_stock: allocatedStock,
                                  daily_run_rate_7: product.daily_run_rate_7 * stockProportion,
                                  daily_run_rate_14: product.daily_run_rate_14 * stockProportion,
                                  daily_run_rate_30: product.daily_run_rate_30 * stockProportion,
                                  weighted_daily_run_rate: product.weighted_daily_run_rate * stockProportion,
                                  demand_growth_rate: 0,
                                  forecast_stock_90_days: 0,
                                  oos_date: null,
                                  reorder_flagged: false,
                                  minimum_reorder_amount: 0,
                                  manufacturing_time_days: product.manufacturing_time_days,
                                  shipping_time_days: product.shipping_time_days,
                                  platforms: platforms,
                                  record_count: records.length,
                                  in_stock_rate_90: null,
                                  in_stock_rate_180: null,
                                  warehouse_breakdown: warehouse_breakdown
                                });
                              });
                            }

                          }

                          return (
                            <React.Fragment key={index}>
                              {/* Main Product Row - Clickable to expand */}
                              <tr
                                onClick={() => {
                                  toggleProductExpansion(productName);
                                }}
                                className={`hover:bg-gray-50 transition-colors cursor-pointer ${product.reorder_flagged ? "bg-red-50" : ""}`}
                                title={isExpanded ? "Click to collapse SKU details" : "Click to expand and see SKU breakdown, in-stock rates, and graphs"}
                              >
                                <td className={`sticky left-0 z-10 px-6 py-4 whitespace-nowrap shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] ${product.reorder_flagged ? "bg-red-50" : "bg-white"}`}>
                                  <div className="flex items-center space-x-2">
                                    <button
                                      className="flex items-center justify-center w-8 h-8 rounded-lg pointer-events-none transition-colors"
                                      title={isExpanded ? "Collapse SKU details" : "Expand to see SKU breakdown, in-stock rates, and graphs"}
                                    >
                                      {false ? (
                                        <svg className="w-5 h-5 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
                                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                      ) : (
                                        <svg
                                          className={`w-5 h-5 transition-all duration-200 ${isExpanded ? 'rotate-90 text-blue-600' : 'text-gray-600'}`}
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                                        </svg>
                                      )}
                                    </button>
                                    <div>
                                      <div className="text-sm font-medium text-gray-900">
                                        {product.product_name}
                                      </div>
                                      <div className="text-sm text-gray-500">
                                        {product.sku_count} SKUs
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {product.current_stock?.toLocaleString() || '0'}
                                </td>
                                <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {product.daily_run_rate_7?.toFixed(2) || '0.00'}
                                </td>
                                <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {product.daily_run_rate_14?.toFixed(2) || '0.00'}
                                </td>
                                <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {product.daily_run_rate_30?.toFixed(2) || '0.00'}
                                </td>
                                <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {product.weighted_daily_run_rate?.toFixed(2) || '0.00'}
                                </td>
                                <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {((product.demand_growth_rate || 0) * 100).toFixed(2)}%
                                </td>
                                <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {product.allocated_stock?.toLocaleString() || '0'}
                                </td>
                                <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {product.forecast_stock_90_days?.toLocaleString() || '0'}
                                </td>
                                <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {formatDate(product.oos_date)}
                                </td>
                                <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                                  <span
                                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${product.reorder_flagged
                                      ? "bg-red-100 text-red-800"
                                      : "bg-green-100 text-green-800"
                                      }`}
                                  >
                                    {product.reorder_flagged ? "REORDER NOW" : "OK"}
                                  </span>
                                </td>
                                <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {product.minimum_reorder_amount?.toLocaleString() || '0'}
                                </td>
                                <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {product.manufacturing_time_days || 'N/A'}
                                </td>
                                <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {product.shipping_time_days || 'N/A'}
                                </td>
                                {/* Empty cells for SKU-only columns */}
                                <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-center text-sm text-gray-400">
                                  <span className="text-xs">Expand for details</span>
                                </td>
                                <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-center text-sm text-gray-400">
                                  <span className="text-xs">Expand for details</span>
                                </td>
                                <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-center text-sm text-gray-400">
                                  <span className="text-xs">↓</span>
                                </td>
                              </tr>

                              {/* SKU Detail Rows */}
                              {isExpanded && (
                                queryLocalLoading ? (
                                  // Loading Skeleton for SKU Details
                                  Array.from({ length: PRODUCT_SKU_MAPPING[productName]?.length || 3 }).map((_, skuIndex) => (
                                    <tr key={`loading-${index}-${skuIndex}`} className="bg-gray-50 border-l-4 border-blue-200 animate-pulse">
                                      <td className="sticky left-0 z-10 px-4 lg:px-6 py-3 whitespace-nowrap pl-12 bg-gray-50 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                                        <div className="flex items-center space-x-2">
                                          <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                                          <div>
                                            <div className="h-4 bg-gray-300 rounded w-20 mb-1"></div>
                                            <div className="h-3 bg-gray-200 rounded w-32"></div>
                                          </div>
                                        </div>
                                      </td>
                                      <td className="px-4 lg:px-6 py-3 whitespace-nowrap">
                                        <div className="h-4 bg-gray-300 rounded w-16"></div>
                                      </td>
                                      <td className="px-4 lg:px-6 py-3 whitespace-nowrap">
                                        <div className="h-4 bg-gray-300 rounded w-12"></div>
                                      </td>
                                      <td className="px-4 lg:px-6 py-3 whitespace-nowrap">
                                        <div className="h-4 bg-gray-300 rounded w-12"></div>
                                      </td>
                                      <td className="px-4 lg:px-6 py-3 whitespace-nowrap">
                                        <div className="h-4 bg-gray-300 rounded w-12"></div>
                                      </td>
                                      <td className="px-4 lg:px-6 py-3 whitespace-nowrap">
                                        <div className="h-4 bg-gray-300 rounded w-12"></div>
                                      </td>
                                      <td className="px-4 lg:px-6 py-3 whitespace-nowrap">
                                        <div className="h-4 bg-gray-300 rounded w-12"></div>
                                      </td>
                                      <td className="px-4 lg:px-6 py-3 whitespace-nowrap">
                                        <div className="h-4 bg-gray-300 rounded w-12"></div>
                                      </td>
                                      <td className="px-4 lg:px-6 py-3 whitespace-nowrap">
                                        <div className="h-4 bg-gray-300 rounded w-16"></div>
                                      </td>
                                      <td className="px-4 lg:px-6 py-3 whitespace-nowrap">
                                        <div className="h-4 bg-gray-300 rounded w-20"></div>
                                      </td>
                                      <td className="px-4 lg:px-6 py-3 whitespace-nowrap">
                                        <div className="h-6 bg-gray-300 rounded-full w-16"></div>
                                      </td>
                                      <td className="px-4 lg:px-6 py-3 whitespace-nowrap">
                                        <div className="h-4 bg-gray-300 rounded w-16"></div>
                                      </td>
                                      <td className="px-4 lg:px-6 py-3 whitespace-nowrap">
                                        <div className="h-4 bg-gray-300 rounded w-8"></div>
                                      </td>
                                      <td className="px-4 lg:px-6 py-3 whitespace-nowrap">
                                        <div className="h-4 bg-gray-300 rounded w-8"></div>
                                      </td>
                                    </tr>
                                  ))
                                ) : hasRawDataError ? (
                                  // Error loading raw data
                                  <tr key={`error-${index}`} className="bg-red-50 border-l-4 border-red-200">
                                    <td colSpan={14} className="px-4 lg:px-6 py-4 text-center text-sm text-gray-500">
                                      <div className="flex items-center justify-center space-x-2">
                                        <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <span>Error loading SKU data for {product.product_name}: {queryLocalError?.message || 'Unknown error'}</span>
                                        <button
                                          onClick={() => {
                                            refetchLocalData();
                                          }}
                                          className="ml-2 text-blue-600 hover:text-blue-800 underline text-xs"
                                        >
                                          Retry
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ) : skuDetails.length === 0 ? (
                                  // No data available (but not loading or error)
                                  <tr key={`no-data-${index}`} className="bg-yellow-50 border-l-4 border-yellow-200">
                                    <td colSpan={14} className="px-4 lg:px-6 py-4 text-center text-sm text-gray-500">
                                      <div className="flex items-center justify-center space-x-2">
                                        <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                                        </svg>
                                        <span>No SKU data available for {product.product_name} (sku_breakdown is empty)</span>
                                      </div>
                                    </td>
                                  </tr>
                                ) : (
                                  // Actual SKU Data
                                  skuDetails.map((skuDetail: any, skuIndex: number) => (
                                    <React.Fragment key={`${index}-${skuIndex}`}>
                                      <tr className="bg-gray-50 border-l-4 border-blue-200">
                                        <td className="sticky left-0 z-10 px-4 lg:px-6 py-3 whitespace-nowrap pl-12 bg-gray-50 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                                          <div className="flex items-center space-x-2">
                                            <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                                            <div>
                                              <div className="text-sm font-medium text-gray-700">
                                                {skuDetail.sku}
                                              </div>
                                              <div className="text-xs text-gray-500">
                                                {(Array.isArray(skuDetail.platforms)
                                                  ? skuDetail.platforms.join(', ')
                                                  : skuDetail.platforms) || 'No stock'}
                                              </div>
                                            </div>
                                          </div>
                                        </td>
                                        <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-gray-700">
                                          {skuDetail.current_stock?.toLocaleString() || '0'}
                                        </td>
                                        <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-gray-700">
                                          {skuDetail.daily_run_rate_7?.toFixed(2) || '0.00'}
                                        </td>
                                        <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-gray-700">
                                          {skuDetail.daily_run_rate_14?.toFixed(2) || '0.00'}
                                        </td>
                                        <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-gray-700">
                                          {skuDetail.daily_run_rate_30?.toFixed(2) || '0.00'}
                                        </td>
                                        <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-gray-700">
                                          {skuDetail.weighted_daily_run_rate?.toFixed(2) || '0.00'}
                                        </td>
                                        <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-gray-700">
                                          {((skuDetail.demand_growth_rate || 0) * 100).toFixed(2)}%
                                        </td>
                                        <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-gray-700">
                                          {skuDetail.allocated_stock?.toLocaleString() || '0'}
                                        </td>
                                        <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-gray-700">
                                          {skuDetail.forecast_stock_90_days?.toLocaleString() || '0'}
                                        </td>
                                        <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-gray-700">
                                          {skuDetail.oos_date ? formatDate(skuDetail.oos_date) : 'N/A'}
                                        </td>
                                        <td className="px-4 lg:px-6 py-3 whitespace-nowrap">
                                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${skuDetail.reorder_flagged ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"
                                            }`}>
                                            {skuDetail.reorder_flagged ? "REORDER" : "OK"}
                                          </span>
                                        </td>
                                        <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-gray-700">
                                          {skuDetail.minimum_reorder_amount?.toLocaleString() || '0'}
                                        </td>
                                        <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-gray-700">
                                          {skuDetail.manufacturing_time_days || 25}
                                        </td>
                                        <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-gray-700">
                                          {skuDetail.shipping_time_days || 45}
                                        </td>
                                        <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-center text-sm">
                                          {skuDetail.in_stock_rate_90 !== null ? (
                                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${skuDetail.in_stock_rate_90 >= 95 ? 'bg-green-100 text-green-800' :
                                              skuDetail.in_stock_rate_90 >= 80 ? 'bg-yellow-100 text-yellow-800' :
                                                'bg-red-100 text-red-800'
                                              }`}>
                                              {skuDetail.in_stock_rate_90.toFixed(1)}%
                                            </span>
                                          ) : isSkuHistoryLoading ? (
                                            <span className="inline-flex h-6 w-14 animate-pulse rounded-full bg-gray-200" />
                                          ) : (
                                            <span className="text-gray-400">N/A</span>
                                          )}
                                        </td>
                                        <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-center text-sm">
                                          {skuDetail.in_stock_rate_180 !== null ? (
                                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${skuDetail.in_stock_rate_180 >= 95 ? 'bg-green-100 text-green-800' :
                                              skuDetail.in_stock_rate_180 >= 80 ? 'bg-yellow-100 text-yellow-800' :
                                                'bg-red-100 text-red-800'
                                              }`}>
                                              {skuDetail.in_stock_rate_180.toFixed(1)}%
                                            </span>
                                          ) : isSkuHistoryLoading ? (
                                            <span className="inline-flex h-6 w-14 animate-pulse rounded-full bg-gray-200" />
                                          ) : (
                                            <span className="text-gray-400">N/A</span>
                                          )}
                                        </td>
                                        <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-center text-sm">
                                          {skuDetail.in_stock_rate_180 !== null ? (
                                            (() => {
                                              const oosRate = 100 - skuDetail.in_stock_rate_180;
                                              return (
                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${oosRate <= 5 ? 'bg-green-100 text-green-800' :
                                                  oosRate <= 10 ? 'bg-yellow-100 text-yellow-800' :
                                                    'bg-red-100 text-red-800'
                                                  }`}>
                                                  {oosRate.toFixed(1)}%
                                                </span>
                                              );
                                            })()
                                          ) : isSkuHistoryLoading ? (
                                            <span className="inline-flex h-6 w-14 animate-pulse rounded-full bg-gray-200" />
                                          ) : (
                                            <span className="text-gray-400">N/A</span>
                                          )}
                                        </td>
                                        <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-center">
                                          <button
                                            onClick={() => setStockGraphSku({ sku: skuDetail.sku, warehouse: selectedWarehouse })}
                                            className="inline-flex items-center justify-center p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                            title="View stock history graph"
                                          >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                            </svg>
                                          </button>
                                        </td>
                                      </tr>
                                      {skuDetail.warehouse_breakdown && skuDetail.warehouse_breakdown.length > 1 && (
                                        <tr className="bg-gray-50 border-l-4 border-indigo-300 border-t border-gray-200">
                                          <td colSpan={18} className="px-4 lg:px-6 py-2.5 pl-12 bg-indigo-50/30">
                                            <div className="flex flex-wrap items-center gap-3 ml-6">
                                              <span className="text-xs font-semibold text-indigo-600 uppercase tracking-widest flex items-center">
                                                <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                                </svg>
                                                Location Breakdown:
                                              </span>
                                              {skuDetail.warehouse_breakdown.map((wh: any, wIndex: number) => (
                                                <div key={`wh-${wIndex}`} className="flex items-center space-x-2 bg-white px-3 py-1.5 rounded-md shadow-sm border border-indigo-100">
                                                  <span className="w-2 h-2 rounded-full bg-indigo-400"></span>
                                                  <span className="text-sm font-medium text-gray-700">{wh.warehouse}</span>
                                                  <span className="text-sm font-bold text-gray-900 bg-gray-100 px-1.5 rounded">{wh.stock.toLocaleString()}</span>
                                                </div>
                                              ))}
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  ))
                                )
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {/* Central Stock Management - Expandable by Product */}
              {activeSection === "central" && centralStockData && (
                <>
                  {/* Mobile Card Layout for Central Management - Expandable */}
                  <div className="block sm:hidden space-y-4">
                    {centralStockData.products.map((product, productIndex) => {
                      const isExpanded = expandedCentralProducts.has(product.product_name);
                      const hasReorderNeeded = product.reorder_flagged; // ✅ FIXED: Use product-level flag (already accounts for in-progress)
                      const totalAmountRequired = product.reorder_amount_required; // ✅ FIXED: Use product-level amount (already deducted in-progress)

                      return (
                        <div key={product.product_name} className={`rounded-lg border ${hasReorderNeeded ? "border-red-200 bg-red-50" : "border-gray-200 bg-gray-50"}`}>
                          {/* Product Header - Always Visible */}
                          <div
                            className="p-4 cursor-pointer hover:bg-opacity-80 transition-colors"
                            onClick={() => {
                              if (isExpanded) {
                                setExpandedCentralProducts(prev => {
                                  const newSet = new Set(prev);
                                  newSet.delete(product.product_name);
                                  return newSet;
                                });
                              } else {
                                setExpandedCentralProducts(prev => new Set(prev).add(product.product_name));
                              }
                            }}
                          >
                            <div className="flex justify-between items-center">
                              <div className="flex items-center space-x-3">
                                <div className="flex items-center justify-center w-6 h-6 rounded hover:bg-gray-100 transition-colors">
                                  <svg
                                    className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                </div>
                                <h3 className="font-semibold text-gray-900">{product.product_name}</h3>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-600">{product.warehouses.length} warehouses</span>
                                <span
                                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${hasReorderNeeded
                                    ? "bg-red-100 text-red-800"
                                    : "bg-green-100 text-green-800"
                                    }`}
                                >
                                  {hasReorderNeeded ? "REORDER NEEDED" : "ALL OK"}
                                </span>
                              </div>
                            </div>
                            <div className="mt-2 text-sm text-gray-600">
                              Total Amount Required: <span className="font-medium">{totalAmountRequired.toLocaleString()}</span>
                              <span className="ml-1 text-xs text-gray-500">(104 days supply)</span>
                            </div>
                          </div>

                          {/* Warehouse Details - Expandable */}
                          {isExpanded && (
                            <div className="border-t border-gray-200 bg-white">
                              {product.warehouses.map((warehouse, warehouseIndex) => (
                                <div key={warehouse.warehouse} className={`p-4 border-b border-gray-100 last:border-b-0 ${warehouse.reorder_flagged ? "bg-red-25" : ""}`}>
                                  <div className="flex justify-between items-center mb-3">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                      {warehouse.warehouse}
                                    </span>
                                    <span
                                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${warehouse.reorder_flagged
                                        ? "bg-red-100 text-red-800"
                                        : "bg-green-100 text-green-800"
                                        }`}
                                    >
                                      {warehouse.reorder_flagged ? "REORDER NEEDED" : "ALL OK"}
                                    </span>
                                  </div>
                                  <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                      <span className="text-gray-500">Current Stock:</span>
                                      <div className="font-medium">{warehouse.current_stock?.toLocaleString() || '0'}</div>
                                    </div>
                                    <div>
                                      <span className="text-gray-500">Avg Daily RR:</span>
                                      <div className="font-medium">{warehouse.daily_run_rate?.toFixed(2) || '0.00'}</div>
                                    </div>
                                    <div>
                                      <span className="text-gray-500">Days til OOS:</span>
                                      <div className="font-medium">
                                        {warehouse.days_until_oos !== undefined && warehouse.days_until_oos !== null
                                          ? Math.round(warehouse.days_until_oos)
                                          : 'N/A'}
                                      </div>
                                    </div>
                                    <div>
                                      <span className="text-gray-500">Reorder Amount:</span>
                                      <div className="font-medium">{warehouse.reorder_amount?.toLocaleString() || '0'}</div>
                                    </div>
                                    {warehouse.reorder_amount > 0 && warehouse.cutoff_date && warehouse.cutoff_date !== "N/A" && (
                                      <div className="col-span-2">
                                        <span className="text-gray-500">Cutoff Date:</span>
                                        <div className="font-medium">{formatDate(warehouse.cutoff_date)}</div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Central Management - Grouped by Product Series */}
                  <div className="hidden sm:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Product Name
                          </th>
                          <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Warehouse
                          </th>
                          <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Current Stock
                          </th>
                          <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Average Daily RR
                          </th>
                          <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Days til OOS
                          </th>
                          <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Reorder Status
                          </th>
                          <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Reorder Amount Required
                          </th>
                          <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Cutoff Date
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {centralStockData.products.map((product) => {
                          const isExpanded = expandedCentralProducts.has(product.product_name);
                          const hasReorderNeeded = product.reorder_flagged;

                          // Calculate aggregated metrics for the product series
                          const totalStock = product.warehouses.reduce((sum, w) => sum + (w.current_stock || 0), 0);
                          const totalDailyRR = product.warehouses.reduce((sum, w) => sum + (w.daily_run_rate || 0), 0);
                          const minDaysOOS = Math.min(...product.warehouses.map(w => w.days_until_oos || Infinity).filter(d => d !== Infinity));
                          const avgDaysOOS = totalDailyRR > 0 ? Math.floor(totalStock / totalDailyRR) : 0;
                          const totalReorderAmount = product.reorder_amount_required || 0;
                          const earliestCutoff = product.earliest_cutoff_date;

                          return (
                            <React.Fragment key={product.product_name}>
                              {/* Product Header Row with Aggregated Metrics - Clickable to expand */}
                              <tr
                                onClick={() => {
                                  if (isExpanded) {
                                    setExpandedCentralProducts(prev => {
                                      const newSet = new Set(prev);
                                      newSet.delete(product.product_name);
                                      return newSet;
                                    });
                                  } else {
                                    setExpandedCentralProducts(prev => new Set(prev).add(product.product_name));
                                  }
                                }}
                                className={`${hasReorderNeeded ? "bg-red-50" : "bg-gray-50"} font-semibold hover:bg-opacity-80 cursor-pointer transition-colors`}
                                title={isExpanded ? "Click to collapse warehouse details" : "Click to expand and see warehouse breakdown"}
                              >
                                <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                                  <div className="flex items-center space-x-2 w-full text-left">
                                    <svg
                                      className={`w-5 h-5 text-gray-500 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                    <span className="font-semibold">{product.product_name}</span>
                                  </div>
                                </td>
                                <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-gray-600">
                                  {product.warehouses.length} locations
                                </td>
                                <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-gray-900 font-semibold">
                                  {totalStock.toLocaleString()}
                                </td>
                                <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-gray-900 font-semibold">
                                  {(totalDailyRR || 0).toFixed(2)}
                                </td>
                                <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-gray-900 font-semibold">
                                  {totalDailyRR > 0 ? avgDaysOOS : 'N/A'}
                                </td>
                                <td className="px-4 lg:px-6 py-3 whitespace-nowrap">
                                  <span
                                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${hasReorderNeeded
                                      ? "bg-red-100 text-red-800"
                                      : "bg-green-100 text-green-800"
                                      }`}
                                  >
                                    {hasReorderNeeded ? "REORDER NEEDED" : "ALL OK"}
                                  </span>
                                </td>
                                <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-gray-900 font-semibold">
                                  {totalReorderAmount.toLocaleString()}
                                </td>
                                <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                                  {earliestCutoff && earliestCutoff !== "N/A" ? formatDate(earliestCutoff) : 'N/A'}
                                </td>
                              </tr>

                              {/* Warehouse Detail Rows - Expandable */}
                              {isExpanded && product.warehouses.map((warehouse) => (
                                <tr
                                  key={`${product.product_name}-${warehouse.warehouse}`}
                                  className={`${warehouse.reorder_flagged ? "bg-red-25" : ""} border-l-4 ${warehouse.reorder_flagged ? "border-red-300" : "border-gray-200"}`}
                                >
                                  <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-gray-700">
                                    <div className="pl-8 text-gray-400">└─</div>
                                  </td>
                                  <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                      {warehouse.warehouse}
                                    </span>
                                  </td>
                                  <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                                    {warehouse.current_stock?.toLocaleString() || '0'}
                                  </td>
                                  <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                                    {warehouse.daily_run_rate?.toFixed(2) || '0.00'}
                                  </td>
                                  <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                                    {warehouse.days_until_oos !== undefined && warehouse.days_until_oos !== null
                                      ? Math.round(warehouse.days_until_oos)
                                      : 'N/A'}
                                  </td>
                                  <td className="px-4 lg:px-6 py-3 whitespace-nowrap">
                                    <span
                                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${warehouse.reorder_flagged
                                        ? "bg-red-100 text-red-800"
                                        : "bg-green-100 text-green-800"
                                        }`}
                                    >
                                      {warehouse.reorder_flagged ? "REORDER NEEDED" : "ALL OK"}
                                    </span>
                                  </td>
                                  <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                                    {warehouse.reorder_amount?.toLocaleString() || '0'}
                                  </td>
                                  <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                                    {warehouse.reorder_amount > 0 && warehouse.cutoff_date && warehouse.cutoff_date !== "N/A"
                                      ? formatDate(warehouse.cutoff_date)
                                      : 'N/A'}
                                  </td>
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {/* Orders to be Placed*/}
              {activeSection === "orders" && (
                <>
                  {ordersData ? (
                    <>
                      {/* Orders Summary Text */}
                      <div className="mb-6">
                        <p className="text-gray-600 text-sm">
                          {ordersData.total_orders_needed} orders needed • {ordersData.urgent_orders.length} urgent (≤7 days)
                        </p>
                      </div>

                      {/* Mobile Card Layout for Orders */}
                      <div className="block sm:hidden space-y-4">
                        {ordersData.orders.map((order, index) => (
                          <div key={index} className="bg-gray-50 rounded-lg p-4 space-y-3">
                            <div className="flex justify-between items-center border-b pb-2">
                              <h3 className="font-semibold text-gray-900">{order.product_name}</h3>
                              <span className={`px-2 py-1 rounded text-xs font-medium ${order.days_until_cutoff <= 7 ? "bg-red-100 text-red-800" :
                                order.days_until_cutoff <= 14 ? "bg-yellow-100 text-yellow-800" :
                                  "bg-green-100 text-green-800"
                                }`}>
                                {order.days_until_cutoff <= 7 ? "URGENT" :
                                  order.days_until_cutoff <= 14 ? "MEDIUM" : "LOW"}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div className="col-span-2">
                                <span className="text-gray-500">Quantity:</span>
                                {editingOrderIndex === index ? (
                                  <div className="flex items-center space-x-2 mt-1">
                                    <input
                                      type="number"
                                      value={editedQuantities[index] !== undefined ? editedQuantities[index] : order.quantity_needed}
                                      onChange={(e) => {
                                        const newQty = parseInt(e.target.value) || 0;
                                        setEditedQuantities({ ...editedQuantities, [index]: newQty });
                                      }}
                                      className="flex-1 px-2 py-1 border border-blue-300 rounded focus:ring-2 focus:ring-blue-500"
                                    />
                                    <button
                                      onClick={() => {
                                        const newQty = editedQuantities[index] !== undefined ? editedQuantities[index] : order.quantity_needed;
                                        const updatedOrders = { ...ordersData };

                                        //  Store edit in session state only, not in order data
                                        updatedOrders.orders[index].quantity_needed = newQty;
                                        const pricePerUnit = order.estimated_cost / order.quantity_needed;
                                        updatedOrders.orders[index].estimated_cost = newQty * pricePerUnit;
                                        setOrdersData(updatedOrders);
                                        setEditedQuantities({ ...editedQuantities, [index]: newQty });
                                        setEditingOrderIndex(null);
                                      }}
                                      className="text-green-600"
                                    >
                                      ✓
                                    </button>
                                    <button
                                      onClick={() => setEditingOrderIndex(null)}
                                      className="text-red-600"
                                    >
                                      ✕
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex items-center space-x-2 mt-1">
                                    <span className="font-medium">{order.quantity_needed.toLocaleString()}</span>
                                    <button
                                      onClick={() => {
                                        setEditingOrderIndex(index);
                                        setEditedQuantities({ ...editedQuantities, [index]: order.quantity_needed });
                                      }}
                                      className="text-blue-600 text-xs"
                                    >
                                      ✎ Edit
                                    </button>
                                  </div>
                                )}
                              </div>
                              <div>
                                <span className="text-gray-500">Days Left:</span>
                                <span className="ml-2 font-medium">{order.days_until_cutoff}</span>
                              </div>
                              <div className="col-span-2">
                                <span className="text-gray-500">Cutoff:</span>
                                <span className="ml-2 font-medium">{formatDate(order.order_cutoff_date)}</span>
                              </div>
                            </div>
                            <div className="pt-2 border-t">
                              <div className="flex justify-between items-center">
                                <div>
                                  <span className="text-gray-500 text-sm">Est. Cost:</span>
                                  <span className="ml-2 font-medium">{formatCurrency(order.estimated_cost)}</span>
                                </div>
                                {order.quantity_needed > 0 ? (
                                  <button
                                    onClick={() => handleConfirmOrder(order)}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md text-xs font-medium transition-colors"
                                  >
                                    Confirm Order
                                  </button>
                                ) : (
                                  <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium">
                                    ✓ Fully Ordered
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Desktop Table Layout for Orders */}
                      <div className="hidden sm:block overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Product Name
                              </th>
                              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Quantity Needed
                              </th>
                              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Order Cutoff Date
                              </th>
                              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Days Until Cutoff
                              </th>
                              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Priority
                              </th>
                              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Estimated Cost
                              </th>
                              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {ordersData.orders.map((order, index) => (
                              <tr key={index} className={order.priority === "urgent" ? "bg-red-50" : ""}>
                                <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {order.product_name}
                                </td>
                                <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  <div className="flex flex-col items-start">
                                    {editingOrderIndex === index ? (
                                      <div className="flex items-center space-x-2">
                                        <input
                                          type="number"
                                          value={editedQuantities[index] !== undefined ? editedQuantities[index] : order.quantity_needed}
                                          onChange={(e) => {
                                            const newQty = parseInt(e.target.value) || 0;
                                            setEditedQuantities({ ...editedQuantities, [index]: newQty });
                                          }}
                                          className="w-32 px-2 py-1 border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                          autoFocus
                                        />
                                        <button
                                          onClick={() => {
                                            // Save the edited quantity
                                            const newQty = editedQuantities[index] !== undefined ? editedQuantities[index] : order.quantity_needed;
                                            const updatedOrders = { ...ordersData };

                                            //  Store edit in session state only, not in order data
                                            updatedOrders.orders[index].quantity_needed = newQty;

                                            // Update estimated cost based on new quantity
                                            const pricePerUnit = order.estimated_cost / order.quantity_needed;
                                            updatedOrders.orders[index].estimated_cost = newQty * pricePerUnit;

                                            setOrdersData(updatedOrders);
                                            setEditedQuantities({ ...editedQuantities, [index]: newQty });
                                            setEditingOrderIndex(null);

                                          }}
                                          className="text-green-600 hover:text-green-800"
                                          title="Save"
                                        >
                                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                          </svg>
                                        </button>
                                        <button
                                          onClick={() => {
                                            setEditingOrderIndex(null);
                                            setEditedQuantities({ ...editedQuantities, [index]: order.quantity_needed });
                                          }}
                                          className="text-red-600 hover:text-red-800"
                                          title="Cancel"
                                        >
                                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                          </svg>
                                        </button>
                                      </div>
                                    ) : (
                                      <div className="flex items-center space-x-2">
                                        <span className="font-semibold">{order.quantity_needed.toLocaleString()}</span>
                                        <button
                                          onClick={() => {
                                            setEditingOrderIndex(index);
                                            setEditedQuantities({ ...editedQuantities, [index]: order.quantity_needed });
                                          }}
                                          className="text-blue-600 hover:text-blue-800"
                                          title="Edit quantity"
                                        >
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                          </svg>
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {formatDate(order.order_cutoff_date)}
                                </td>
                                <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {order.days_until_cutoff} days
                                </td>
                                <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                                  <span
                                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${order.priority === "urgent"
                                      ? "bg-red-100 text-red-800"
                                      : "bg-yellow-100 text-yellow-800"
                                      }`}
                                  >
                                    {order.priority.toUpperCase()}
                                  </span>
                                </td>
                                <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {formatCurrency(order.estimated_cost)}
                                </td>
                                <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm font-medium">
                                  {order.quantity_needed > 0 ? (
                                    <button
                                      onClick={() => handleConfirmOrder(order)}
                                      className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md text-xs font-medium transition-colors"
                                    >
                                      Confirm Order
                                    </button>
                                  ) : (
                                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded-md text-xs font-medium">
                                      ✓ Fully Ordered
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                      <div className="flex flex-col items-center">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Orders Data Available</h3>
                        <p className="text-gray-500 mb-4">Unable to load orders to be placed. The API might be unavailable.</p>
                        <button
                          onClick={loadOrdersData}
                          className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition duration-300 ease-in-out"
                          disabled={loading}
                        >
                          {loading ? "Loading..." : "Retry"}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Order Management - Combines "Orders to Place" + In-Progress Tracking */}
              {activeSection === "order-management" && (
                <>
                  <UnifiedOrderManagement />
                </>
              )}

              {/* Inventory Planning - Replenishment & Forecast */}
              {activeSection === "replenishment" && (
                <>
                  <ReplenishmentRecommendations viewMode={forecastViewMode} />
                </>
              )}

              {/* Shipping Analytics */}
              {activeSection === "shipping-analytics" && (
                <>
                  <ShippingAnalytics />
                </>
              )}

              {/* Product Analytics */}
              {activeSection === "product-analytics" && (
                <>
                  <ProductMixChart className="mb-6" />
                </>
              )}

              {activeSection === "unit-economics" && (
                <Settings />
              )}
            </>
          )}
        </div>
      </div>

      {/* Stock Graph Modal */}
      {stockGraphSku && (
        <StockGraphModal
          sku={stockGraphSku.sku}
          warehouse={stockGraphSku.warehouse}
          onClose={() => setStockGraphSku(null)}
        />
      )}
    </div>
  );
};

export default Dashboard;
